import clsx from 'clsx'
import { ReactNode } from 'react'

export type HeaderColor =
  | 'base'
  | 'red'
  | 'orange'
  | 'yellow'
  | 'green'
  | 'blue'
  | 'purple'
  | 'grey'

export interface CardHeaderProps {
  /** Left side icon */
  icon?: ReactNode
  /** Main title text */
  title: string | ReactNode
  /** Optional subtitle text */
  subtitle?: string | ReactNode
  /** Callback when the header (or action icon) is clicked */
  onAction?: () => void
  /** Optional custom icon to show on the right when onAction is provided (defaults to chevron icon) */
  actionIcon?: ReactNode
  /** Color theme for the header */
  color?: HeaderColor
  /** Whether the parent card is flat (affects border radius) */
  flat?: boolean
}

export function CardHeader({
  icon,
  title,
  subtitle,
  onAction,
  actionIcon,
  color = 'base',
  flat = false,
}: CardHeaderProps) {
  const getHeaderColorClasses = (headerColor: HeaderColor) => {
    const colorMap = {
      base: 'bg-themed-base-700 text-themed-white',
      red: 'bg-themed-red-600 text-themed-white',
      orange: 'bg-themed-orange-500 text-themed-white',
      yellow: 'bg-themed-yellow-500 text-themed-base-900',
      green: 'bg-themed-green-900 text-themed-white',
      blue: 'bg-themed-blue-500 text-themed-white',
      purple: 'bg-themed-purple-500 text-themed-white',
      grey: 'bg-themed-grey-500 text-themed-white',
    }
    return colorMap[headerColor]
  }

  const classes = {
    header: clsx(
      'flex items-center gap-2 px-4 py-4 -mx-6 mb-4',
      getHeaderColorClasses(color),
      {
        // Round top corners only if card has rounded corners and not flat
        'rounded-t-2.5xl': !flat,
        'cursor-pointer select-none': Boolean(onAction),
      },
    ),
    headerIcon: clsx(
      'flex-shrink-0 self-start w-5 h-5 [&>svg]:w-full [&>svg]:h-full',
    ),
    headerContent: clsx('flex-1 min-w-0'), // min-w-0 for text truncation
    headerTitle: clsx('font-semibold leading-tight text-sm'),
    headerSubtitle: clsx('text-body3 mt-1'),
    headerAction: clsx(
      'flex-shrink-0 w-5 h-5 [&>svg]:w-full [&>svg]:h-full focus:outline-none focus:ring-2 focus:ring-white/50 rounded',
    ),
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!onAction) {
      return
    }
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      onAction()
    }
  }

  const actionable = Boolean(onAction)
  const showRightIcon = actionable

  return (
    <div
      className={classes.header}
      role={actionable ? 'button' : undefined}
      tabIndex={actionable ? 0 : undefined}
      aria-pressed={actionable ? false : undefined}
      onClick={actionable ? onAction : undefined}
      onKeyDown={actionable ? handleKeyDown : undefined}
    >
      {icon && <div className={classes.headerIcon}>{icon}</div>}
      <div className={classes.headerContent}>
        <div className={classes.headerTitle}>{title}</div>
        {subtitle && <div className={classes.headerSubtitle}>{subtitle}</div>}
      </div>
      {showRightIcon && (
        <div
          className={classes.headerAction}
          aria-label={actionable ? 'Card action' : undefined}
        >
          {actionIcon || (
            <svg
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="w-full h-full"
              aria-hidden="true"
            >
              <path
                d="M7.5 5L12.5 10L7.5 15"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          )}
        </div>
      )}
    </div>
  )
}

CardHeader.displayName = 'CardHeader'
